/**
 * DetailViewItem2 Component
 *
 * Displays a single photo in the detail view grid with interactive zoom functionality.
 *
 * Features:
 * - Single-click to zoom (fit ↔ 100%) with zoom cursor indicator
 * - Trackpad pinch-to-zoom gestures (minimum zoom is fit level)
 * - Smart URL selection with debounced loading (waits for zoom to stabilize)
 * - Loading indicators during resolution switches
 * - Graceful error state for missing URLs
 *
 * @module DetailViewItem2
 */

import React, { useCallback, useMemo, useContext, useState } from 'react';
import { TransformWrapper, TransformComponent, type ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import logger from '@/utils/logger';
import { cn } from '@/lib/utils';
import { usePixelPerfectZoom } from '@/hooks/usePixelPerfectZoom';
import { ZoomPanContext } from '@/contexts/ZoomPanContext';
import { useDevFlags } from '@/contexts/DevFlagsContext';
import { Spinner } from '@/components/atoms';
import type { DetailViewItem2Props } from './DetailViewItem2.types';
import {
  adaptPhotoData,
  getVariantTypeFromUrl,
  validateUrlConfiguration,
  validateVariantDimensions,
} from './DetailViewItem2.utils';
import { DebugOverlay } from './DetailViewItem2.DebugOverlay';
import { DebugModeOnly } from '../DebugModeOnly';

// Pixels of mouse movement to consider it a drag (pan) vs a click
const DRAG_THRESHOLD = 5;

/**
 * DetailViewItem2 - Interactive photo display with zoom
 *
 * User Interactions:
 * - Single-click image → Zoom to 100% (or back to fit if already zoomed)
 * - Cursor: zoom-in at fit level, grab/hand when zoomed (for panning)
 * - Drag to pan (when zoomed) → Navigate around the image (doesn't trigger zoom toggle)
 * - Trackpad pinch-to-zoom → Smoothly zoom with minimum at fit level
 *
 * @example
 * ```tsx
 * <DetailViewItem2
 *   photo={photoData}
 *   className="aspect-video"
 * />
 * ```
 */
export const DetailViewItem2: React.FC<DetailViewItem2Props> = React.memo(
  ({ photo, className, onContextMenu, ...restProps }) => {
    // State for debug overlay
    const [loadedDimensions, setLoadedDimensions] = useState<{ width: number; height: number } | null>(null);

    // Dev flags for controlling mouse zoom behavior
    // Feature 656: Mouse zoom is disabled by default, can be enabled via Dev Flags menu
    const devFlags = useDevFlags();
    const mouseZoomEnabled = devFlags?.flags.mouseZoom ?? false;

    // Focus point state (percentage 0-100, where 50/50 is center)
    // Feature 646: Track which part of the image is centered in viewport
    const [focusPoint, setFocusPoint] = useState({ x: 50, y: 50 });

    // Track when we're programmatically applying focus point to prevent reentrancy
    const isApplyingFocusRef = React.useRef(false);

    // Track drag state to distinguish click from pan gesture
    const mouseDownPosRef = React.useRef<{ x: number; y: number } | null>(null);
    const wasDraggingRef = React.useRef(false);

    // Validate variants is not stringified (detect data pipeline issues)
    if (typeof photo.photoData.variants === 'string') {
      logger.error(
        'DetailViewItem2',
        'Photo variants is stringified - data pipeline issue detected',
        {
          photoId: photo.photoData.id,
          variantsType: typeof photo.photoData.variants,
          variantsValue: photo.photoData.variants,
          // Include stack trace to help identify where the component is being used
          componentStack: new Error().stack,
        }
      );
    }

    // Convert PhotoItem2ViewModel to Photo format for hook
    const adaptedPhoto = useMemo(() => adaptPhotoData(photo), [photo]);

    // Get shared zoom/pan context
    const sharedState = useContext(ZoomPanContext);

    // Initialize pixel-perfect zoom hook with translation layer
    const {
      ourZoomLevel: zoomLevel,
      imageSource,
      isLoading,
      transformScale,
      transformRef,
      imageRef,
      containerRef,
      containerSize,
      handleImageLoad: handleImageLoadForHook,
      handleTransformed: handleTransformedForHook,
      getCursorStyle,
    } = usePixelPerfectZoom({
      photo: adaptedPhoto,
      sharedState: sharedState || undefined,
    });

    // Determine which variant is currently being displayed
    const currentVariantType = useMemo(
      () => getVariantTypeFromUrl(imageSource, photo, loadedDimensions),
      [imageSource, photo, loadedDimensions]
    );

    // Validate URL configuration (zoom-aware and container-size-aware)
    const urlValidation = useMemo(() => {
      return validateUrlConfiguration(
        imageSource,
        photo,
        currentVariantType,
        zoomLevel,
        containerSize.width > 0 ? containerSize.width : undefined,
        containerSize.height > 0 ? containerSize.height : undefined
      );
    }, [imageSource, photo, currentVariantType, zoomLevel, containerSize]);

    // Validate that the loaded dimensions match the expected variant
    const dimensionValidation = useMemo(() => {
      if (!loadedDimensions || currentVariantType === 'unknown') return null;
      return validateVariantDimensions(
        loadedDimensions.width,
        loadedDimensions.height,
        currentVariantType,
        photo.photoData.variants
      );
    }, [loadedDimensions, currentVariantType, photo.photoData.variants]);

    /**
     * Handle image load to capture dimensions and notify hook
     */
    const handleImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
      const img = event.currentTarget;

      // Set dimensions for debug overlay
      setLoadedDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });

      // Notify hook to calculate initial scale factor
      handleImageLoadForHook(img);
    }, [handleImageLoadForHook]);

    /**
     * Calculate focus point from TransformWrapper position
     * Feature 646: Track which part of image is centered in viewport
     *
     * Focus point is percentage-based (0-100):
     * - (50, 50) = center of image
     * - (0, 0) = top-left corner
     * - (100, 100) = bottom-right corner
     */
    const updateFocusPoint = useCallback(() => {
      // Skip if we're programmatically applying focus point (prevents reentrancy)
      if (isApplyingFocusRef.current) {
        logger.debug('Focus', 'Skipping update - programmatic focus apply in progress');
        return;
      }

      const container = containerRef.current;
      const img = imageRef.current;
      const transform = transformRef.current;

      if (!container || !img || !transform || zoomLevel === 'fit') {
        // At fit mode, always centered
        logger.debug('Focus', 'At fit mode, setting to 50/50');
        setFocusPoint({ x: 50, y: 50 });
        return;
      }

      const { positionX, positionY, scale } = transform.instance.transformState;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const imageWidth = img.clientWidth * scale;
      const imageHeight = img.clientHeight * scale;

      // Calculate which part of the image is at the center of the viewport
      // positionX/Y are the transform translate values (can be negative)
      // Center of viewport in image coordinates: (containerWidth/2 - positionX)
      const centerX = containerWidth / 2 - positionX;
      const centerY = containerHeight / 2 - positionY;

      // Convert to percentage (0-100)
      const focusX = (centerX / imageWidth) * 100;
      const focusY = (centerY / imageHeight) * 100;

      const newFocusPoint = {
        x: Math.max(0, Math.min(100, focusX)),
        y: Math.max(0, Math.min(100, focusY))
      };

      logger.debug('Focus', 'Calculated from transform:', {
        positionX,
        positionY,
        scale,
        containerWidth,
        containerHeight,
        imageWidth,
        imageHeight,
        focusPoint: newFocusPoint
      });

      setFocusPoint(newFocusPoint);
    }, [zoomLevel]);

    /**
     * Apply focus point by setting TransformWrapper position
     * Feature 646: Maintain same part of image centered when zoom/resize
     */
    const applyFocusPoint = useCallback(() => {
      const container = containerRef.current;
      const img = imageRef.current;
      const transform = transformRef.current;

      if (!container || !img || !transform || zoomLevel === 'fit') {
        // Can't apply focus at fit mode (centered automatically)
        logger.debug('Focus', 'Cannot apply at fit mode');
        return;
      }

      const { scale } = transform.instance.transformState;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const imageWidth = img.clientWidth * scale;
      const imageHeight = img.clientHeight * scale;

      // Calculate transform position to center focus point in viewport
      // We want: containerWidth/2 - positionX = focusPoint.x% of imageWidth
      // Therefore: positionX = containerWidth/2 - (focusPoint.x/100 * imageWidth)
      const positionX = containerWidth / 2 - (focusPoint.x / 100) * imageWidth;
      const positionY = containerHeight / 2 - (focusPoint.y / 100) * imageHeight;

      logger.debug('Focus', 'Applying focus point:', {
        focusPoint,
        containerWidth,
        containerHeight,
        imageWidth,
        imageHeight,
        scale,
        calculatedPosition: { positionX, positionY }
      });

      // Set flag to prevent handleTransformed from creating feedback loop
      isApplyingFocusRef.current = true;

      // Set transform position (TransformWrapper will clamp to bounds)
      transform.setTransform(positionX, positionY, scale, 0); // 0ms = instant

      // Clear flag after next frame (no cleanup needed - requestAnimationFrame is one-shot)
      requestAnimationFrame(() => {
        isApplyingFocusRef.current = false;
      });
    }, [focusPoint, zoomLevel]);

    /**
     * Integrate with TransformWrapper's onTransformed callback
     * Updates focus point when user pans/zooms with trackpad
     */
    const handleTransformed = useCallback((ref: ReactZoomPanPinchRef) => {
      // Notify hook for zoom translation
      handleTransformedForHook(ref);

      // Update focus point from new transform state
      updateFocusPoint();
    }, [handleTransformedForHook, updateFocusPoint]);

    /**
     * Set focus to 50/50 when entering fit mode
     * Feature 646: Fit mode is always centered
     */
    React.useEffect(() => {
      if (zoomLevel === 'fit') {
        logger.debug('Focus', 'Entering fit mode, setting to 50/50');
        setFocusPoint({ x: 50, y: 50 });
      }
    }, [zoomLevel]);

    /**
     * Maintain focus point when zoom changes
     * Feature 646: Keep same part of image centered
     */
    React.useEffect(() => {
      // Wait for image to render at new size, then apply focus point
      const timeoutId = setTimeout(() => {
        applyFocusPoint();
      }, 0);

      return () => clearTimeout(timeoutId);
    }, [applyFocusPoint, zoomLevel]);

    /**
     * Maintain focus point on window resize
     */
    React.useEffect(() => {
      const handleResize = () => {
        // Debounce via requestAnimationFrame
        requestAnimationFrame(() => {
          applyFocusPoint();
        });
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [applyFocusPoint]);

    /**
     * Track mouse down position to detect drag vs click
     */
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
      wasDraggingRef.current = false;
    }, []);

    /**
     * Track mouse movement to detect if user is dragging (panning)
     */
    const handleMouseMove = useCallback((e: React.MouseEvent) => {
      // Skip if no mousedown or already detected drag
      if (!mouseDownPosRef.current || wasDraggingRef.current) return;

      const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
      const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);

      if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
        wasDraggingRef.current = true;
      }
    }, []);

    /**
     * Single-click handler to toggle between fit and 100% zoom
     * Only triggers if user didn't drag (pan) the image
     */
    const handleClick = useCallback(() => {
      // Reset mouse tracking
      mouseDownPosRef.current = null;

      // If user was dragging/panning, don't toggle zoom
      if (wasDraggingRef.current) {
        wasDraggingRef.current = false;
        return;
      }

      // Toggle between fit and 100% via shared state
      const newZoom = zoomLevel === 'fit' ? 100 : 'fit';
      sharedState?.onZoomChange(newZoom);
    }, [zoomLevel, sharedState]);

    // No URL available - display error state
    if (!imageSource) {
      return (
        <div
          className={cn(
            'w-full h-full flex items-center justify-center',
            'bg-surface-1 text-muted rounded',
            'min-h-[200px]',
            className
          )}
          onContextMenu={onContextMenu}
          {...restProps}
        >
          <p>Image not available</p>
        </div>
      );
    }

    // Display image with TransformWrapper for pan/zoom (Feature 646: enhanced)
    return (
      <div
        ref={containerRef}
        className={cn('w-full h-full min-h-[200px] relative', className)}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
        onContextMenu={onContextMenu}
        style={{ cursor: getCursorStyle() }}
        {...restProps}
      >
        {/* Loading spinner overlay during resolution switches */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-20">
            <Spinner size="lg" />
          </div>
        )}

        {/* TransformWrapper for pan/zoom with trackpad gestures */}
        {/* Feature 656: Mouse zoom disabled by default, toggle via Dev Flags menu */}
        <TransformWrapper
          ref={transformRef}
          initialScale={1}
          minScale={1}
          maxScale={4}
          limitToBounds={true}
          centerOnInit={true}
          doubleClick={{
            disabled: true, // Use custom double-click handler
          }}
          wheel={{
            disabled: !mouseZoomEnabled, // Feature 656: Disable wheel zoom unless flag is enabled
            step: 0.1,        // Mouse wheel zoom increment
            smoothStep: 0.005, // Trackpad pinch smoothness
          }}
          pinch={{
            disabled: !mouseZoomEnabled, // Feature 656: Disable pinch zoom unless flag is enabled
          }}
          panning={{
            disabled: zoomLevel === 'fit', // Only pan when zoomed
          }}
          onTransformed={handleTransformed}
        >
          <TransformComponent
            wrapperClass="!w-full !h-full"
            contentClass="!w-full !h-full flex items-center justify-center"
          >
            <img
              ref={imageRef}
              src={imageSource}
              alt={photo.photoData.description || 'Photo detail'}
              onLoad={handleImageLoad}
              loading="lazy"
              className="w-full h-full object-contain"
              style={{
                userSelect: 'none',
              }}
              draggable={false}
            />
          </TransformComponent>
        </TransformWrapper>

        {/* Debug Overlay - shows current resolution and zoom level */}
        <DebugModeOnly>
          <DebugOverlay
            loadedDimensions={loadedDimensions}
            currentVariantType={currentVariantType}
            zoomLevel={zoomLevel}
            globalId={photo.photoData.id}
            urlValidation={urlValidation}
            dimensionValidation={dimensionValidation}
            transformScale={transformScale}
            focusPoint={focusPoint}
          />
        </DebugModeOnly>
      </div>
    );
  }
);

DetailViewItem2.displayName = 'DetailViewItem2';
